// src/components/AgendaListaMovil.js
import React, { useState, useMemo } from 'react';
import MarcaRecordatorio from './agenda/MarcaRecordatorio';
import { useAuth } from '../context/AuthContext';
import { getIniciales } from '../utils/formatos';
import { etiquetaMotivoBloqueo } from '../hooks/useBloqueos';

// ============================================================================
// AGENDA — VISTA MÓVIL TIPO APP
// En lugar de la rejilla ancha (que en móvil obliga a hacer scroll en dos ejes),
// muestra una lista vertical del día: chips para elegir médico, filtro rápido
// (Todos / Libres / Ocupados) para localizar el hueco al instante, y los tramos
// agrupados en Mañana / Tarde con cabeceras fijas.
// ============================================================================

const CORTE_TARDE = '15:00';

const ESTILO_ESTADO = {
    en_curso:  { dot: 'bg-blue-500 animate-pulse', chip: 'bg-blue-50 text-blue-700 border-blue-200', label: 'En curso' },
    completada:{ dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Completada' },
    ausente:   { dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200', label: 'Ausente' },
    pendiente: { dot: 'bg-amber-400', chip: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Pendiente' }
};

export default function AgendaListaMovil({
    medicosVisibles = [],
    horasLaborables = [],
    obtenerCitaEspecifica,
    fecha,
    onAbrirEstacion,
    onNuevaCita,
    onVerExpediente,
    onCobrarConsulta,
    onCancelarCita,
    onMarcarAusente,
    obtenerBloqueo
}) {
    const [medicoActivoId, setMedicoActivoId] = useState(medicosVisibles[0]?.id || null);
    const [filtro, setFiltro] = useState('todos'); // 'todos' | 'libres' | 'ocupados'

    // Si cambia la plantilla visible y el activo ya no está, reseteamos
    const medicoActivo = medicosVisibles.find(m => String(m.id) === String(medicoActivoId)) || medicosVisibles[0];
    const medIdActivo = medicoActivo?.id;

    // ¿El tramo horario ya pasó? (para no dejar agendar en el pasado)
    const esPasado = (hora) => {
        const [h, m] = hora.split(':');
        const base0 = new Date(fecha); base0.setHours(0, 0, 0, 0);
        const hoy0 = new Date(); hoy0.setHours(0, 0, 0, 0);
        if (base0 < hoy0) return true;
        if (base0 > hoy0) return false;
        const finSlot = new Date(fecha); finSlot.setHours(parseInt(h, 10), parseInt(m, 10) + 30, 0, 0);
        return new Date() > finSlot;
    };

    const slots = useMemo(() => {
        if (!medIdActivo) return [];
        return horasLaborables.map(hora => {
            const cita = obtenerCitaEspecifica(hora, medIdActivo);
            const bloqueo = !cita && obtenerBloqueo ? obtenerBloqueo(hora, medIdActivo) : null;
            return { hora, cita, bloqueo, ocupado: Boolean(cita || bloqueo), pasado: esPasado(hora) };
        }).filter(s => {
            if (filtro === 'libres') return !s.ocupado && !s.pasado;
            if (filtro === 'ocupados') return s.ocupado;
            return true;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [horasLaborables, medIdActivo, filtro, fecha, obtenerCitaEspecifica, obtenerBloqueo]);

    const libresCount = useMemo(() => {
        if (!medIdActivo) return 0;
        return horasLaborables.filter(h => !obtenerCitaEspecifica(h, medIdActivo) && !(obtenerBloqueo && obtenerBloqueo(h, medIdActivo)) && !esPasado(h)).length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [horasLaborables, medIdActivo, fecha, obtenerCitaEspecifica, obtenerBloqueo]);

    const grupos = useMemo(() => ({
        manana: slots.filter(s => s.hora < CORTE_TARDE),
        tarde: slots.filter(s => s.hora >= CORTE_TARDE)
    }), [slots]);

    if (!medicoActivo) {
        return (
            <div className="bg-white border border-slate-200 rounded-3xl p-8 text-center">
                <p className="text-slate-500 font-bold text-sm">No hay profesionales para mostrar en la agenda.</p>
            </div>
        );
    }

    return (
        <div className="w-full space-y-4">
            {/* Selector de médico (chips horizontales) */}
            {medicosVisibles.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 hide-scrollbar">
                    {medicosVisibles.map((m) => {
                        const activo = String(m.id) === String(medIdActivo);
                        return (
                            <button
                                key={m.id}
                                onClick={() => setMedicoActivoId(m.id)}
                                className={`shrink-0 inline-flex items-center gap-2 pl-1.5 pr-3.5 py-1.5 rounded-full border text-sm font-bold transition-all ${activo ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
                            >
                                <span className={`flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-black ${activo ? 'bg-white/15 text-white' : 'bg-slate-900 text-white'}`}>
                                    {getIniciales(m.nombre, m.apellidos)}
                                </span>
                                {m.nombre}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Cabecera del médico activo + filtros */}
            <div className="bg-white border border-slate-200 rounded-3xl p-4 shadow-sm">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="min-w-0">
                        <h3 className="text-base font-black text-slate-900 truncate">{medicoActivo.nombre} {medicoActivo.apellidos}</h3>
                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">{medicoActivo.especialidad || 'General'}</p>
                    </div>
                    <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-black">
                        {libresCount} libres
                    </span>
                </div>

                {/* Filtro rápido */}
                <div className="grid grid-cols-3 gap-1.5 bg-slate-100 p-1 rounded-2xl">
                    {[
                        { id: 'todos', label: 'Todos' },
                        { id: 'libres', label: 'Libres' },
                        { id: 'ocupados', label: 'Ocupados' }
                    ].map(op => (
                        <button
                            key={op.id}
                            onClick={() => setFiltro(op.id)}
                            className={`py-2 rounded-xl text-xs font-black uppercase tracking-wide transition-all ${filtro === op.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                        >
                            {op.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Lista de tramos por período */}
            {slots.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-3xl p-8 text-center">
                    <p className="text-slate-500 font-bold text-sm">
                        {filtro === 'libres' ? 'No quedan huecos libres en esta agenda.' : 'Sin tramos para este filtro.'}
                    </p>
                </div>
            ) : (
                <div className="space-y-5">
                    {[{ id: 'manana', label: 'Mañana', lista: grupos.manana }, { id: 'tarde', label: 'Tarde', lista: grupos.tarde }]
                        .filter(g => g.lista.length > 0)
                        .map(grupo => (
                            <div key={grupo.id}>
                                <div className="sticky top-16 z-10 -mx-1 px-1 py-1.5 bg-slate-50/90 backdrop-blur-sm">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{grupo.label}</span>
                                </div>
                                <div className="space-y-2 mt-1">
                                    {grupo.lista.map(({ hora, cita, bloqueo, pasado }) => (
                                        <SlotMovil
                                            key={hora}
                                            hora={hora}
                                            cita={cita}
                                            bloqueo={bloqueo}
                                            pasado={pasado}
                                            onNuevaCita={() => onNuevaCita && onNuevaCita(hora, medIdActivo)}
                                            onAbrirEstacion={onAbrirEstacion}
                                            onVerExpediente={onVerExpediente}
                                            onCobrarConsulta={onCobrarConsulta}
                                            onCancelarCita={onCancelarCita}
                                            onMarcarAusente={onMarcarAusente}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
}

// Fila de un tramo horario (hueco libre u ocupado por una cita)
function SlotMovil({ hora, cita, bloqueo, pasado, onNuevaCita, onAbrirEstacion, onVerExpediente, onCobrarConsulta, onCancelarCita, onMarcarAusente }) {
    const { veHistoriaClinica } = useAuth();
    // Tramo BLOQUEADO (vacaciones, festivo, formación…)
    if (!cita && bloqueo) {
        return (
            <div className="w-full flex items-center gap-3 p-3 rounded-2xl border border-slate-200 bg-slate-100 text-left">
                <span className="w-14 shrink-0 text-sm font-black text-slate-500 font-mono">{hora}</span>
                <span className="flex-1 min-w-0 text-sm font-bold text-slate-500 truncate">{etiquetaMotivoBloqueo(bloqueo)}</span>
                <span className="shrink-0 px-2 py-0.5 rounded-md border border-slate-300 text-[10px] font-black uppercase tracking-wide text-slate-500">Bloqueado</span>
            </div>
        );
    }

    // Hueco LIBRE
    if (!cita) {
        return (
            <button
                type="button"
                disabled={pasado}
                onClick={onNuevaCita}
                className={`w-full flex items-center gap-3 p-3 rounded-2xl border text-left transition-all ${
                    pasado
                        ? 'bg-slate-50 border-slate-100 opacity-60 cursor-not-allowed'
                        : 'bg-white border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/40'
                }`}
            >
                <span className="w-14 shrink-0 text-sm font-black text-slate-700 font-mono">{hora}</span>
                <span className="flex-1 text-sm font-bold text-slate-400">{pasado ? 'No disponible' : 'Hueco libre'}</span>
                {!pasado && (
                    <span className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 text-[11px] font-black">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"></path></svg>
                        Agendar
                    </span>
                )}
            </button>
        );
    }

    // Tramo OCUPADO por una cita
    const estado = cita.estado || 'pendiente';
    const est = ESTILO_ESTADO[estado] || ESTILO_ESTADO.pendiente;
    const paciente = cita.paciente || {};
    const pacId = paciente.id || paciente._id;

    return (
        <div className="w-full bg-white border border-slate-200 rounded-2xl p-3 shadow-sm">
            <div className="flex items-start gap-3">
                <button
                    type="button"
                    onClick={() => onAbrirEstacion && onAbrirEstacion(cita)}
                    className="flex items-start gap-3 flex-1 min-w-0 text-left"
                >
                    <span className="w-14 shrink-0 text-sm font-black text-slate-700 font-mono pt-0.5">{hora}</span>
                    <span className="flex-1 min-w-0">
                        <span className="block text-sm font-black text-slate-900 truncate">
                            {paciente.nombre} {paciente.apellidos}
                        </span>
                        <span className="block text-xs font-semibold text-slate-500 truncate mt-0.5">
                            {cita.motivo || 'Consulta médica'}
                        </span>
                        {cita.recordatorio && <span className="block mt-1"><MarcaRecordatorio cita={cita} grande /></span>}
                    </span>
                </button>
                <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-black uppercase tracking-wide ${est.chip}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${est.dot}`}></span>
                    {est.label}
                </span>
            </div>

            {/* Acciones rápidas */}
            <div className="flex flex-wrap items-center gap-2 mt-2.5 pt-2.5 border-t border-slate-100">
                <button
                    type="button"
                    onClick={() => onVerExpediente && onVerExpediente(pacId)}
                    className="flex-1 py-2 rounded-xl bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200 transition-colors"
                >
                    Expediente
                </button>
                {estado === 'completada' ? (
                    <button
                        type="button"
                        onClick={() => onCobrarConsulta && onCobrarConsulta(cita)}
                        className="flex-1 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors"
                    >
                        Cobrar
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => onAbrirEstacion && onAbrirEstacion(cita)}
                        className="flex-1 py-2 rounded-xl bg-clinica-primary text-white text-xs font-bold hover:bg-blue-700 transition-colors"
                    >
                        {veHistoriaClinica ? 'Atender' : 'Ver ficha'}
                    </button>
                )}
                {/* 🎟️ "Ausente" gasta la sesión del bono; "Cancelar" la devuelve.
                    Ambos confirman antes de nada (ver Agenda.js). */}
                {estado !== 'completada' && estado !== 'ausente' && (
                    <button
                        type="button"
                        onClick={() => onMarcarAusente && onMarcarAusente(cita)}
                        className="shrink-0 px-3 py-2 rounded-xl bg-rose-100 text-rose-700 text-xs font-bold hover:bg-rose-600 hover:text-white transition-colors"
                    >
                        Ausente
                    </button>
                )}
                {estado !== 'completada' && estado !== 'cancelada' && (
                    <button
                        type="button"
                        onClick={() => onCancelarCita && onCancelarCita(cita)}
                        className="shrink-0 px-3 py-2 rounded-xl bg-amber-100 text-amber-700 text-xs font-bold hover:bg-amber-600 hover:text-white transition-colors"
                    >
                        Cancelar
                    </button>
                )}
            </div>
        </div>
    );
}
